import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Globe2, MapPin, Plane } from "lucide-react";
import { REGIONS, CAUSES } from "../../data/regions";
import { Reveal, SectionHeading, Counter, Eyebrow } from "../common";

export default function WhereWeWork() {
  const [key, setKey] = useState(REGIONS[1].key);
  const region = REGIONS.find((r) => r.key === key) || REGIONS[0];

  const totalCountries = REGIONS.reduce((n, r) => n + r.countries.length, 0);
  const totalProjects = REGIONS.reduce(
    (n, r) => n + r.countries.reduce((m, c) => m + c.projects, 0),
    0
  );

  return (
    <section id="where" className="bg-ivi-mint py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Where can you go?"
          title="Six regions. One application fee."
          text="Pick a region to see the countries we operate in, how many projects are running and what the work looks like on the ground."
        />

        <Reveal className="mt-10">
          <div className="mx-auto flex max-w-2xl justify-center gap-8">
            <div className="text-center">
              <p className="font-heading text-[34px] font-extrabold leading-none text-ivi-greenDark">
                <Counter value={totalCountries} />
              </p>
              <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivi-slateSoft">
                Countries
              </p>
            </div>
            <div className="w-px bg-ivi-mintDeep" />
            <div className="text-center">
              <p className="font-heading text-[34px] font-extrabold leading-none text-ivi-greenDark">
                <Counter value={totalProjects} suffix="+" />
              </p>
              <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivi-slateSoft">
                Live projects
              </p>
            </div>
            <div className="w-px bg-ivi-mintDeep" />
            <div className="text-center">
              <p className="font-heading text-[34px] font-extrabold leading-none text-ivi-greenDark">
                <Counter value={6} />
              </p>
              <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivi-slateSoft">
                Regions
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-[300px_1fr]">
          {/* Region rail */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {REGIONS.map((r) => {
              const on = r.key === key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setKey(r.key)}
                  className={`group flex shrink-0 items-center gap-3 rounded-2xl px-5 py-4 text-left transition-[background-color,color,transform] duration-200 lg:w-full lg:shrink ${
                    on
                      ? "bg-ivi-green text-white shadow-[0_14px_30px_-18px_rgba(90,158,47,1)]"
                      : "bg-ivi-sand text-ivi-slate hover:-translate-y-0.5 hover:bg-ivi-mint"
                  }`}
                >
                  <span
                    className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                      on ? "bg-white/20 text-white" : "bg-white text-ivi-greenDark"
                    }`}
                  >
                    <Globe2 className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block whitespace-nowrap font-heading text-[15px] font-bold lg:whitespace-normal">
                      {r.name}
                    </span>
                    <span
                      className={`block text-[12px] font-semibold ${
                        on ? "text-white/75" : "text-ivi-slateSoft"
                      }`}
                    >
                      {r.countries.length}{" "}
                      {r.countries.length === 1 ? "country" : "countries"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Map panel */}
          <Reveal>
            <div className="overflow-hidden rounded-[28px] bg-ivi-sand shadow-[0_20px_54px_-38px_rgba(24,40,32,0.6)] ring-1 ring-ivi-mintDeep">
              <div className="grid md:grid-cols-2">
                <div className="relative bg-white p-6">
                  <img
                    key={region.map}
                    src={region.map}
                    alt={`${region.name} map`}
                    className="h-[260px] w-full object-contain"
                    style={{ animation: "heroUp 0.6s cubic-bezier(0.22,1,0.36,1) both" }}
                  />
                  <span className="absolute left-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-ivi-mint px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-[0.14em] text-ivi-greenDark">
                    <MapPin className="h-3 w-3" /> {region.name}
                  </span>
                </div>
                <div className="relative min-h-[260px] overflow-hidden">
                  <img
                    key={region.photo}
                    src={region.photo}
                    alt={`Volunteers in ${region.name}`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/85 to-ivi-slate/10" />
                  <p className="absolute inset-x-6 bottom-6 text-[15px] leading-relaxed text-white">
                    {region.blurb}
                  </p>
                </div>
              </div>

              <div className="p-7">
                <p className="font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-ivi-slateSoft">
                  Destinations in {region.name}
                </p>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {region.countries.map((c) =>
                    c.to ? (
                      <Link
                        key={c.name}
                        to={c.to}
                        className="group inline-flex items-center gap-2 rounded-full bg-ivi-green px-4 py-2.5 font-heading text-[13px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                      >
                        {c.name}
                        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px]">
                          {c.projects}
                        </span>
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-45" />
                      </Link>
                    ) : (
                      <span
                        key={c.name}
                        className="inline-flex cursor-default items-center gap-2 rounded-full border border-ivi-mintDeep bg-white px-4 py-2.5 font-heading text-[13px] font-bold text-ivi-slate transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-ivi-green"
                      >
                        {c.name}
                        <span className="rounded-full bg-ivi-mint px-2 py-0.5 text-[11px] text-ivi-greenDark">
                          {c.projects}
                        </span>
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* What can you do */}
        <div className="mt-20">
          <div className="flex flex-col items-center text-center">
            <Eyebrow>What can you do?</Eyebrow>
            <h3 className="mt-4 font-heading text-[28px] font-extrabold text-ivi-slate sm:text-[34px]">
              Six ways to be useful
            </h3>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAUSES.map((c, i) => (
              <Reveal key={c.name} delay={i * 70}>
                <div className="group relative h-56 overflow-hidden rounded-3xl">
                  <img
                    src={c.img}
                    alt={c.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-110"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/92 via-ivi-slate/35 to-transparent" />
                  <div className="absolute inset-x-6 bottom-6">
                    <p className="font-heading text-[19px] font-extrabold text-white">{c.name}</p>
                    <p className="mt-1.5 max-h-0 overflow-hidden text-[14px] leading-relaxed text-white/80 transition-[max-height] duration-500 group-hover:max-h-24">
                      {c.text}
                    </p>
                  </div>
                  <span className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white ring-1 ring-white/30 backdrop-blur transition-transform duration-300 group-hover:rotate-45">
                    <Plane className="h-4 w-4" />
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
